/**
 * MarsMate — Navbar Scroll Behavior Hook
 *
 * Provides:
 *   - scrolled: true when page has scrolled past threshold (for glass effect)
 *   - hidden: true when scrolling down fast (auto-hide)
 *   - scrollY: raw scroll position
 *   - progress: 0–1 page scroll progress
 *
 * Usage:
 *   import { useNavbarScroll } from '../animations/useNavbarScroll';
 *
 *   function Navbar() {
 *     const { scrolled, hidden, scrollY } = useNavbarScroll();
 *
 *     return (
 *       <motion.nav
 *         animate={{ y: hidden ? -80 : 0, opacity: hidden ? 0 : 1 }}
 *         style={{
 *           position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100,
 *           background: scrolled ? 'rgba(8,11,20,0.85)' : 'transparent',
 *           backdropFilter: scrolled ? 'blur(20px)' : 'none',
 *           borderBottom: scrolled ? '1px solid rgba(193, 68, 14,0.15)' : 'none',
 *           transition: 'background 0.3s ease, border-color 0.3s ease',
 *         }}
 *       >
 *         ...
 *       </motion.nav>
 *     );
 *   }
 */

import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * @param {object} opts
 * @param {number} [opts.threshold=80]      - px scroll before "scrolled" is true
 * @param {number} [opts.hideThreshold=200] - px scroll before hide-on-scroll activates
 * @param {number} [opts.hideVelocity=5]    - min px/frame to trigger hide
 */
export function useNavbarScroll({
  threshold = 80,
  hideThreshold = 200,
  hideVelocity = 5,
} = {}) {
  const [scrollY, setScrollY]     = useState(0);
  const [scrolled, setScrolled]   = useState(false);
  const [hidden, setHidden]       = useState(false);
  const [progress, setProgress]   = useState(0);
  const lastY = useRef(0);
  const ticking = useRef(false);

  const handleScroll = useCallback(() => {
    if (ticking.current) return;
    ticking.current = true;

    requestAnimationFrame(() => {
      const currentY = window.scrollY;
      const delta = currentY - lastY.current;

      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const prog = docHeight > 0 ? Math.min(currentY / docHeight, 1) : 0;

      setScrollY(currentY);
      setScrolled(currentY > threshold);
      setProgress(prog);

      // Hide on fast scroll-down, show on any scroll-up
      if (currentY > hideThreshold) {
        if (delta > hideVelocity) {
          setHidden(true);
        } else if (delta < -hideVelocity) {
          setHidden(false);
        }
      } else {
        setHidden(false);
      }

      lastY.current = currentY;
      ticking.current = false;
    });
  }, [threshold, hideThreshold, hideVelocity]);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  return { scrollY, scrolled, hidden, progress };
}

/**
 * Framer Motion variant factories for navbar transitions.
 * Import and spread into <motion.nav animate={navbarMotion(scrolled, hidden)}>
 */
export function navbarMotion(scrolled, hidden) {
  return {
    y: hidden ? -80 : 0,
    opacity: hidden ? 0 : 1,
  };
}

export const navbarTransition = {
  duration: 0.28,
  ease: [0.22, 1, 0.36, 1],
};

/**
 * CSS-in-JS styles for the navbar container.
 * Spread into the style prop.
 */
export function navbarStyle(scrolled) {
  return {
    position: 'fixed',
    top: 0, left: 0, right: 0,
    zIndex: 100,
    padding: scrolled ? '12px 24px' : '20px 24px',
    background: scrolled
      ? 'rgba(8, 11, 20, 0.88)'
      : 'transparent',
    backdropFilter: scrolled ? 'blur(20px) saturate(180%)' : 'none',
    WebkitBackdropFilter: scrolled ? 'blur(20px) saturate(180%)' : 'none',
    borderBottom: scrolled
      ? '1px solid rgba(193, 68, 14, 0.14)'
      : '1px solid transparent',
    boxShadow: scrolled
      ? '0 4px 30px rgba(0, 0, 0, 0.35)'
      : 'none',
    transition:
      'background 0.35s ease, backdrop-filter 0.35s ease, padding 0.35s ease, box-shadow 0.35s ease, border-color 0.35s ease',
  };
}
